import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import MarkdownEditor from "@/components/board/MarkdownEditor";
import Attachments from "@/components/board/Attachments";
import {
  Calendar as CalendarIcon,
  CheckSquare,
  Link as LinkIcon,
  Move,
  Play,
  Tag,
  Trash2,
  Unlink,
  User,
  X,
} from "lucide-react";

const LABEL_COLORS = {
  urgent: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', border: 'border-red-300 dark:border-red-700' },
  feature: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-300 dark:border-blue-700' },
  bug: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-300 dark:border-orange-700' },
  design: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300 dark:border-purple-700' },
  research: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', border: 'border-green-300 dark:border-green-700' },
  ops: { bg: 'bg-zinc-100 dark:bg-zinc-800', text: 'text-zinc-700 dark:text-zinc-300', border: 'border-zinc-300 dark:border-zinc-600' },
};

function getLabelStyle(label) {
  return LABEL_COLORS[label.toLowerCase()] || {
    bg: 'bg-zinc-100 dark:bg-zinc-800',
    text: 'text-zinc-700 dark:text-zinc-300',
    border: 'border-zinc-300 dark:border-zinc-600',
  };
}

export default function CardDetailModal({
  open,
  onOpenChange,
  cardId,
  lists = [],
  onUpdated,
  onDeleted,
  onMoveCard,
  currentUser,
  board,
}) {
  const [card, setCard] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [newChecklist, setNewChecklist] = useState("");
  const [comment, setComment] = useState("");
  const [syncBoards, setSyncBoards] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncMenuOpen, setSyncMenuOpen] = useState(false);
  const [cardSyncs, setCardSyncs] = useState([]);
  const syncRef = useRef(null);

  useEffect(() => {
    if (!open || !cardId) return;
    setLoading(true);
    api
      .get(`/cards/${cardId}`)
      .then(({ data }) => {
        setCard(data.card);
        setComments(data.comments || []);
        setAttachments(data.attachments || []);
        setTitle(data.card.title);
        // Load syncs
        if (data.card?.id) {
          api.get(`/cards/${data.card.id}/syncs`).then(({ data: syncData }) => {
            setCardSyncs(syncData || []);
          }).catch(() => {});
        }
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load card"))
      .finally(() => setLoading(false));
  }, [open, cardId]);

  const persist = async (patch) => {
    try {
      const { data } = await api.patch(`/cards/${cardId}`, patch);
      setCard(data);
      onUpdated && onUpdated(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const saveTitle = () => {
    if (title.trim() && title !== card.title) persist({ title: title.trim() });
  };

  const saveDescription = (md) => {
    persist({ description: md });
  };

  const [labelInput, setLabelInput] = useState("");

  const addLabel = () => {
    const l = labelInput.trim();
    if (!l) return;
    const labels = [...(card.labels || []), l];
    persist({ labels });
    setLabelInput("");
  };

  const removeLabel = (l) => {
    const labels = (card.labels || []).filter((x) => x !== l);
    persist({ labels });
  };

  const addChecklistItem = () => {
    const t = newChecklist.trim();
    if (!t) return;
    const checklist = [...(card.checklist || []), { id: crypto.randomUUID(), text: t, done: false }];
    persist({ checklist });
    setNewChecklist("");
  };

  const toggleChecklist = (id) => {
    const checklist = card.checklist.map((i) => (i.id === id ? { ...i, done: !i.done } : i));
    persist({ checklist });
  };

  const removeChecklist = (id) => {
    const checklist = card.checklist.filter((i) => i.id !== id);
    persist({ checklist });
  };

  const setDueDate = (date) => {
    persist({ due_date: date ? date.toISOString() : null });
  };

  const setStartDate = (date) => {
    const d = date ? date.toISOString().split('T')[0] : null;
    persist({ start_date: d });
  };

  const submitComment = async (e) => {
    e.preventDefault();
    const t = comment.trim();
    if (!t) return;
    try {
      const { data } = await api.post(`/cards/${cardId}/comments`, { text: t });
      setComments((c) => [...c, data]);
      setComment("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const removeComment = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      setComments((c) => c.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const deleteCard = async () => {
    if (!window.confirm("Move this card to trash? You can restore it within 15 days.")) return;
    try {
      await api.delete(`/cards/${cardId}`);
      onDeleted && onDeleted(cardId);
      onOpenChange(false);
      toast.success("Card moved to trash");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const canDelete = currentUser && board && card && (
    currentUser.id === board.owner_id || currentUser.role === "admin" || currentUser.id === card.created_by
  );

  const breakSync = async () => {
    if (!window.confirm("Break the sync link?")) return;
    try {
      await api.patch(`/cards/${cardId}`, { mirror_id: null });
      setCard((prev) => ({ ...prev, mirror_id: null }));
      toast.success("Sync link broken");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const handleMove = (targetListId) => {
    if (!card || !onMoveCard || targetListId === card.list_id) return;
    onMoveCard(cardId, targetListId);
    setCard({ ...card, list_id: targetListId });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none sm:max-w-3xl max-w-full w-full sm:w-auto h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-y-auto p-0 sm:p-0 inset-0 sm:inset-auto translate-x-0 translate-y-0 sm:translate-x-[-50%] sm:translate-y-[-50%] top-0 left-0 sm:top-1/2 sm:left-1/2"
      >
        <DialogHeader className="p-4 sm:p-6 pb-2 border-b border-zinc-200 dark:border-zinc-800 sticky top-0 bg-white dark:bg-zinc-950 z-10">
          <DialogTitle className="sr-only">Card details</DialogTitle>
          {loading || !card ? (
            <div className="text-sm text-zinc-500 font-mono-tight uppercase tracking-widest">Loading...</div>
          ) : (
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={saveTitle}
              className="w-full font-display text-xl sm:text-2xl font-bold tracking-tight border-none focus:outline-none bg-transparent pr-8 text-zinc-950 dark:text-zinc-100"
              data-testid="card-detail-title-input"
            />
          )}
        </DialogHeader>

        {card && (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-6 p-4 sm:p-6" data-testid="card-detail-modal">
            <div className="space-y-6 min-w-0">
              <div>
                <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Description
                </div>
                <MarkdownEditor
                  value={card.description || ""}
                  onSave={saveDescription}
                />
              </div>

              <Attachments
                cardId={card.id}
                attachments={attachments}
                onChange={setAttachments}
                currentUser={currentUser}
              />

              <div>
                <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
                  <CheckSquare className="h-3 w-3" /> Checklist
                </div>
                <div className="space-y-1.5">
                  {(card.checklist || []).map((it) => (
                    <div key={it.id} className="flex items-center gap-2 group" data-testid={`checklist-item-${it.id}`}>
                      <input
                        type="checkbox"
                        checked={it.done}
                        onChange={() => toggleChecklist(it.id)}
                        className="h-5 w-5 sm:h-4 sm:w-4 accent-black"
                      />
                      <span className={`flex-1 text-sm ${it.done ? "line-through text-zinc-400" : ""}`}>
                        {it.text}
                      </span>
                      <button
                        onClick={() => removeChecklist(it.id)}
                        className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 text-zinc-400 hover:text-red-500 p-1 -m-1"
                        aria-label="Remove item"
                      >
                        <X className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <input
                    value={newChecklist}
                    onChange={(e) => setNewChecklist(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addChecklistItem()}
                    placeholder="Add an item..."
                    className="flex-1 border border-zinc-200 px-3 py-2 text-base sm:text-sm focus:outline-none focus:border-black"
                    data-testid="checklist-add-input"
                  />
                  <button
                    onClick={addChecklistItem}
                    className="px-3 py-2 text-sm bg-black text-white hover:bg-zinc-800"
                    data-testid="checklist-add-button"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div>
                <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Activity / Comments
                </div>
                <form onSubmit={submitComment} className="mb-3">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={2}
                    placeholder="Write a comment..."
                    className="w-full border border-zinc-200 px-3 py-2 text-base sm:text-sm focus:outline-none focus:border-black resize-none"
                    data-testid="comment-input"
                  />
                  <button
                    type="submit"
                    disabled={!comment.trim()}
                    className="mt-2 px-4 py-2 text-xs font-medium bg-black text-white hover:bg-zinc-800 disabled:opacity-50"
                    data-testid="comment-submit"
                  >
                    Comment
                  </button>
                </form>
                <div className="space-y-3">
                  {comments.map((c) => (
                    <div key={c.id} className="border-l-2 border-zinc-200 pl-3" data-testid={`comment-${c.id}`}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium truncate">{c.user_name}</span>
                        <div className="flex items-center gap-2 text-zinc-400 shrink-0">
                          <span className="hidden sm:inline">{new Date(c.created_at).toLocaleString()}</span>
                          <span className="sm:hidden">{new Date(c.created_at).toLocaleDateString()}</span>
                          {currentUser && c.user_id === currentUser.id && (
                            <button
                              onClick={() => removeComment(c.id)}
                              className="hover:text-red-500 p-1 -m-1"
                              aria-label="Delete comment"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="text-sm mt-1 text-zinc-700 whitespace-pre-wrap">{c.text}</div>
                    </div>
                  ))}
                  {comments.length === 0 && (
                    <div className="text-xs text-zinc-400 italic">No comments yet.</div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar - sits below main on mobile, on the right on md+ */}
            <aside className="space-y-4 min-w-0 order-first md:order-last border-b md:border-b-0 md:border-l border-zinc-200 dark:border-zinc-800 pb-4 md:pb-0 md:pl-6 -mx-4 sm:-mx-6 md:mx-0 px-4 sm:px-6 md:px-0">
              {card.created_by_name && (
                <div>
                  <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
                    <User className="h-3 w-3" /> Started by
                  </div>
                  <div className="text-sm text-zinc-700 dark:text-zinc-300">{card.created_by_name}</div>
                </div>
              )}

              {board?.id && (
                <div className="relative" ref={syncRef}>
                  <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
                    <LinkIcon className="h-3 w-3" /> Sync
                  </div>

                  {/* Show all synced boards */}
                  {cardSyncs.length > 0 && (
                    <div className="space-y-1.5 mb-2">
                      {cardSyncs.map((s) => (
                        <div key={s.id} className="flex items-start justify-between gap-1">
                          <div className="min-w-0 flex-1">
                            <div className="text-xs text-green-600 dark:text-green-400 font-medium truncate">
                              {s.board_title || "Board"}
                            </div>
                            {s.card_title && (
                              <div className="text-[10px] text-zinc-500 truncate">{s.card_title}</div>
                            )}
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                await api.delete(`/card-syncs/${s.id}`);
                                setCardSyncs((prev) => prev.filter((x) => x.id !== s.id));
                                toast.success("Sync broken");
                              } catch (err) {
                                toast.error(formatApiError(err.response?.data?.detail) || "Failed");
                              }
                            }}
                            className="text-red-500 hover:text-red-600 shrink-0 mt-0.5"
                            title="Break sync"
                          >
                            <Unlink className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Sync to a new board */}
                  <div className="relative">
                    <button
                      onClick={async () => {
                        setSyncing(true);
                        try {
                          const { data } = await api.get("/boards");
                          // Filter out boards already synced and current board
                          const syncedIds = new Set(cardSyncs.map((s) => s.synced_board_id));
                          const related = (data || []).filter((b) =>
                            b.id !== board.id && !syncedIds.has(b.id) && (b.duplicated_from === board.id || board.duplicated_from === b.id || b.duplicated_from === board.duplicated_from)
                          );
                          setSyncBoards(related);
                          setSyncMenuOpen(true);
                        } catch {}
                        setSyncing(false);
                      }}
                      disabled={syncing}
                      className="w-full border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-xs text-left hover:border-black dark:hover:border-white transition-colors disabled:opacity-50"
                    >
                      {syncing ? "Loading..." : cardSyncs.length > 0 ? "Sync to another board..." : "Sync to board..."}
                    </button>
                    {syncMenuOpen && syncBoards.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 z-20 max-h-48 overflow-y-auto shadow-lg">
                        {syncBoards.map((b) => (
                          <button
                            key={b.id}
                            onClick={async () => {
                              setSyncMenuOpen(false);
                              try {
                                await api.post(`/cards/${cardId}/sync-to-board`, { target_board_id: b.id });
                                toast.success(`Synced to "${b.title}"`);
                                // Reload syncs
                                const { data: syncData } = await api.get(`/cards/${cardId}/syncs`);
                                setCardSyncs(syncData || []);
                              } catch (err) {
                                toast.error(formatApiError(err.response?.data?.detail) || "Sync failed");
                              }
                            }}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800 border-b border-zinc-100 dark:border-zinc-800 last:border-0"
                          >
                            {b.title}
                          </button>
                        ))}
                      </div>
                    )}
                    {syncMenuOpen && syncBoards.length === 0 && !syncing && (
                      <div className="absolute top-full left-0 right-0 mt-1 border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 z-20 p-3 text-xs text-zinc-500 shadow-lg">
                        No related boards found. Duplicate a board first.
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
                  <Move className="h-3 w-3" /> List
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {lists.map((l) => {
                    const active = l.id === card.list_id;
                    return (
                      <button
                        key={l.id}
                        onClick={() => handleMove(l.id)}
                        className={`px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider border transition-colors ${
                          active
                            ? "bg-black dark:bg-white text-white dark:text-black border-black dark:border-white"
                            : "border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:border-black dark:hover:border-white"
                        }`}
                        data-testid={`move-to-list-${l.id}`}
                      >
                        {l.title}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
                  <Play className="h-3 w-3" /> Start date
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="w-full border border-zinc-200 dark:border-zinc-700 px-3 py-2.5 text-sm text-left flex items-center justify-between hover:border-black dark:hover:border-white active:border-black text-zinc-950 dark:text-zinc-100"
                      data-testid="card-detail-start-date-button"
                    >
                      <span className="inline-flex items-center gap-2">
                        <CalendarIcon className="h-3.5 w-3.5" />
                        {card.start_date
                          ? new Date(card.start_date + 'T00:00:00').toLocaleDateString()
                          : "Set start date"}
                      </span>
                      {card.start_date && (
                        <X
                          className="h-3.5 w-3.5 text-zinc-400 hover:text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            setStartDate(null);
                          }}
                        />
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="rounded-none border-zinc-200 dark:border-zinc-800 p-0 w-auto">
                    <Calendar
                      mode="single"
                      selected={card.start_date ? new Date(card.start_date + 'T00:00:00') : undefined}
                      onSelect={setStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Due date
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="w-full border border-zinc-200 dark:border-zinc-700 px-3 py-2.5 text-sm text-left flex items-center justify-between hover:border-black dark:hover:border-white active:border-black text-zinc-950 dark:text-zinc-100"
                      data-testid="card-detail-due-date-button"
                    >
                      <span className="inline-flex items-center gap-2">
                        <CalendarIcon className="h-3.5 w-3.5" />
                        {card.due_date
                          ? new Date(card.due_date).toLocaleDateString()
                          : "Set due date"}
                      </span>
                      {card.due_date && (
                        <X
                          className="h-3.5 w-3.5 text-zinc-400 hover:text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDueDate(null);
                          }}
                        />
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="rounded-none border-zinc-200 dark:border-zinc-800 p-0 w-auto">
                    <Calendar
                      mode="single"
                      selected={card.due_date ? new Date(card.due_date) : undefined}
                      onSelect={setDueDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
                  <Tag className="h-3 w-3" /> Labels
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(card.labels || []).map((l) => {
                    const style = getLabelStyle(l);
                    return (
                      <span
                        key={l}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold uppercase tracking-wider border ${style.bg} ${style.text} ${style.border}`}
                      >
                        {l}
                        <button
                          onClick={() => removeLabel(l)}
                          className="hover:opacity-60"
                          aria-label={`Remove label ${l}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
                <div className="flex gap-1.5">
                  <input
                    value={labelInput}
                    onChange={(e) => setLabelInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLabel(); } }}
                    placeholder="Type label name..."
                    className="flex-1 border border-zinc-200 dark:border-zinc-700 px-2 py-1.5 text-xs focus:outline-none focus:border-black dark:focus:border-white bg-transparent text-zinc-950 dark:text-zinc-100"
                    data-testid="label-input"
                  />
                  <button
                    onClick={addLabel}
                    disabled={!labelInput.trim()}
                    className="px-2 py-1.5 text-xs font-medium bg-black dark:bg-white text-white dark:text-black hover:bg-zinc-800 dark:hover:bg-zinc-200 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>

              {canDelete && (
                <div className="pt-4 border-t border-zinc-200 dark:border-zinc-800">
                  <button
                    onClick={deleteCard}
                    className="w-full inline-flex items-center justify-center gap-2 border border-zinc-200 dark:border-zinc-700 px-3 py-2.5 text-xs text-red-600 hover:border-red-500 active:border-red-500 transition-colors"
                    data-testid="card-detail-delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Move to trash
                  </button>
                </div>
              )}
            </aside>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
